import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { formatCurrency, formatDate, getStatusLabel } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Calendar, CreditCard, CheckCircle, AlertCircle, DoorOpen, Wifi, Car, Droplets, Wind, Tv, Zap, Send, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const facilityIcons = {
  'WiFi': Wifi,
  'Parkir': Car,
  'Kamar Mandi Dalam': Droplets,
  'AC': Wind,
  'TV': Tv,
  'Listrik': Zap,
};

const KamarSaya = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, roomsRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/dashboard`),
        axios.get(`${API_URL}/api/rooms/available/list`),
        axios.get(`${API_URL}/api/room-requests/my`)
      ]);
      setData(dashboardRes.data);
      setAvailableRooms(roomsRes.data);
      setMyRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRoom = (room) => {
    setSelectedRoom(room);
    setRequestMessage('');
    setDialogOpen(true);
  };

  const submitRequest = async () => {
    if (!selectedRoom) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/room-requests`, {
        room_id: selectedRoom.id,
        message: requestMessage
      });
      toast.success('Request kamar berhasil dikirim!');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengirim request');
    } finally {
      setSubmitting(false);
    }
  };

  const hasPendingRequest = myRequests.some(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in" data-testid="kamar-saya-page">
        <div>
          <Skeleton className="h-10 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Skeleton className="h-80 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data?.tenant) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="kamar-saya-page">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Kamar Saya</h1>
          <p className="text-muted-foreground mt-1">Cari dan request kamar yang tersedia</p>
        </div>

        {/* Status Request */}
        {myRequests.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Status Request Anda
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {myRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <p className="font-medium">{req.room_name}</p>
                    <p className="text-sm text-muted-foreground">{formatCurrency(req.room_price)}/bln</p>
                  </div>
                  <Badge className={
                    req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {req.status === 'pending' ? 'Menunggu' : req.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Available Rooms */}
        <div>
          <h2 className="font-heading text-lg font-semibold mb-4">Kamar Tersedia ({availableRooms.length})</h2>
          {availableRooms.length === 0 ? (
            <Card className="p-8">
              <div className="empty-state">
                <Home className="empty-state-icon" />
                <p className="empty-state-title">Tidak ada kamar tersedia</p>
                <p className="empty-state-description">Semua kamar sedang terisi</p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availableRooms.map(room => (
                <Card key={room.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-video relative">
                    <img
                      src={room.image_url || 'https://images.unsplash.com/photo-1614635884840-85cf80d23844?crop=entropy&cs=srgb&fm=jpg&q=85'}
                      alt={room.name}
                      className="w-full h-full object-cover"
                    />
                    <Badge className="absolute top-3 left-3 bg-emerald-500">Tersedia</Badge>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold">{room.name}</h3>
                        <p className="text-sm text-muted-foreground">Lantai {room.floor}</p>
                      </div>
                      <p className="font-bold text-primary">{formatCurrency(room.price)}<span className="text-xs font-normal text-muted-foreground">/bln</span></p>
                    </div>
                    
                    {room.facilities?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {room.facilities.map((facility, i) => {
                          const Icon = facilityIcons[facility] || DoorOpen;
                          return (
                            <div key={i} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                              <Icon className="w-3 h-3" />
                              <span>{facility}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {room.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{room.description}</p>
                    )}

                    <Button 
                      className="w-full rounded-xl"
                      onClick={() => handleRequestRoom(room)}
                      disabled={hasPendingRequest}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {hasPendingRequest ? 'Request Sedang Diproses' : 'Request Kamar Ini'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Request Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Kamar</DialogTitle>
              <DialogDescription>
                Anda akan mengirim request untuk {selectedRoom?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-xl">
                <p className="font-medium">{selectedRoom?.name}</p>
                <p className="text-sm text-muted-foreground">Lantai {selectedRoom?.floor} • {formatCurrency(selectedRoom?.price || 0)}/bln</p>
              </div>
              <div>
                <label className="text-sm font-medium">Pesan (opsional)</label>
                <Textarea
                  placeholder="Tulis pesan untuk pemilik kost..."
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button onClick={submitRequest} disabled={submitting}>
                {submitting ? <div className="w-5 h-5 spinner" /> : 'Kirim Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const { tenant, room, payments } = data;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="kamar-saya-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Kamar Saya</h1>
        <p className="text-muted-foreground mt-1">Informasi kamar yang Anda tempati</p>
      </div>

      {/* Room Info Card */}
      <Card className="overflow-hidden">
        <div className="grid md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto">
            <img
              src={room?.image_url || 'https://images.unsplash.com/photo-1614635884840-85cf80d23844?crop=entropy&cs=srgb&fm=jpg&q=85'}
              alt={room?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <Badge className="w-fit mb-4 bg-primary text-primary-foreground">Kamar Anda</Badge>
            <h2 className="font-heading text-3xl font-bold">{room?.name}</h2>
            <p className="text-muted-foreground mt-1">Lantai {room?.floor}</p>
            
            <div className="mt-6 space-y-4">
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">Harga Sewa</span>
                <span className="font-heading text-xl font-bold text-primary">
                  {formatCurrency(room?.price)}<span className="text-sm font-normal text-muted-foreground">/bulan</span>
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">Mulai Sewa</span>
                <span className="font-medium">{formatDate(tenant?.start_date)}</span>
              </div>
              {tenant?.end_date && (
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-muted-foreground">Berakhir</span>
                  <span className="font-medium">{formatDate(tenant?.end_date)}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-3">
                <span className="text-muted-foreground">Deposit</span>
                <span className="font-medium">{formatCurrency(tenant?.deposit || 0)}</span>
              </div>
            </div>

            {room?.facilities?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm text-muted-foreground mb-3">Fasilitas</p>
                <div className="flex flex-wrap gap-2">
                  {room.facilities.map((f, i) => (
                    <Badge key={i} variant="secondary">{f}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Contract Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Informasi Kontrak
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge className={`status-${tenant?.status}`}>
                {getStatusLabel(tenant?.status)}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">No. KTP</span>
              <span className="font-medium">{tenant?.id_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Telepon</span>
              <span className="font-medium">{tenant?.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{tenant?.email}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="p-6">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Ringkasan Pembayaran
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Tagihan</span>
              <span className="font-medium">{payments?.length || 0} tagihan</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Belum Dibayar</span>
              <span className="font-medium text-amber-600">{data?.pending_count || 0} tagihan</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Tertunggak</span>
              <span className="font-bold text-accent">{formatCurrency(data?.total_pending || 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment History */}
      <Card className="p-6">
        <CardHeader className="p-0 pb-6">
          <CardTitle className="font-heading text-lg">Riwayat Pembayaran Terbaru</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payments?.length > 0 ? (
            <div className="space-y-3">
              {payments.slice(0, 6).map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      payment.status === 'lunas' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                      {payment.status === 'lunas' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {new Date(payment.month + '-01').toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-muted-foreground">{payment.payment_type === 'sewa' ? 'Sewa Kamar' : payment.payment_type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(payment.amount)}</p>
                    <Badge variant={payment.status === 'lunas' ? 'default' : 'secondary'} className={`status-${payment.status}`}>
                      {getStatusLabel(payment.status)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <CreditCard className="empty-state-icon" />
              <p className="empty-state-title">Belum ada pembayaran</p>
              <p className="empty-state-description">Riwayat pembayaran akan muncul di sini</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KamarSaya;
